import * as React from "react";
import { Form, Table, Icon, Dropdown, Popup, Accordion } from "semantic-ui-react";
import _ from "lodash";
import { getDuplicateLiftParameterBindings } from "../../utils/pdApiUtils";

type LiftParameterBindingsProps = {
  code: number;
  bindings: Api.LiftParameterBinding[];
  kceVersions: Api.KceVersions;
  kceProducts: Api.KceProducts;
  handleBindingsChanged: (bindings: Api.LiftParameterBinding[]) => void;
  permissions: boolean;
  storedbindings: Api.LiftParameter;
  editedbindings: Api.LiftParameter;
};

type LiftParameterBindingsState = {
  isOpen: boolean;
};

/** LiftParameterBindings component */
class LiftParameterBindings extends React.Component<
  LiftParameterBindingsProps,
  LiftParameterBindingsState
> {
  state = { isOpen: false };

  render(): JSX.Element {
    const {
      bindings,
      kceVersions,
      kceProducts,
      permissions,
      storedbindings,
      editedbindings
    } = this.props;
    const { isOpen } = this.state;
    const duplicateBindings = getDuplicateLiftParameterBindings(bindings);
    const extendedBindings = bindings
      .sort((a, b) => {
        const verA = kceVersions[a?.kceVersion?.id];
        const verB = kceVersions[b?.kceVersion?.id];
        const prodA = kceProducts[a?.kceProduct?.id];
        const prodB = kceProducts[b?.kceProduct?.id];
        if (a.isDirty || b.isDirty) {
          return 0;
        }
        return verA?.number > verB?.number
          ? -1
          : verA?.number < verB?.number
          ? 1
          : prodA?.productName > prodB?.productName
          ? -1
          : prodA?.productName < prodB?.productName
          ? 1
          : 0;
      })
      .map((binding, index) => {
        if (duplicateBindings.find(duplicateBinding => duplicateBinding === index) !== undefined) {
          return { ...binding, isDuplicate: true };
        } else {
          return { ...binding, isDuplicate: false };
        }
      });

    editedbindings.liftParameterBindings.forEach((editedBinding: Api.LiftParameterBinding) => {
      if (editedBinding.isDirty) {
        delete editedBinding.isDirty;
      }
    });

    // Creates version dropdown options
    const versionOptions = _.values(kceVersions).map(kceVersion => ({
      key: kceVersion.id,
      text: kceVersion.name,
      value: kceVersion.id
    }));

    // Creates product dropdown options
    const productOptions = _.values(kceProducts).map(kceProduct => ({
      key: kceProduct.id,
      text: kceProduct.productName,
      value: kceProduct.id
    }));

    let showMandatory = true;

    if (storedbindings) {
      showMandatory = !_.isEqual(storedbindings, editedbindings);
    }

    return (
      <Form.Group id="toggleLiftParameterBindings" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick={() => this.setState(prevState => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Parameter bindings</b>
              </label>
              {duplicateBindings.length > 0 && (
                <p className="errorMessagePadding">Bindings should not be empty or duplicated.</p>
              )}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell>Product</Table.HeaderCell>
                    {!permissions && (
                      <Table.HeaderCell>
                        <Popup
                          trigger={
                            <Icon
                              id="addBinding"
                              className="color-green"
                              link={true}
                              name="add"
                              onClick={this.handleAddBinding}
                            />
                          }
                          content={"Add binding"}
                        />
                      </Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {extendedBindings.map((binding, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex-row">
                          {binding.isDirty && showMandatory ? "*" : ""}
                          <Dropdown
                            id={`version-${index}`}
                            compact={true}
                            fluid={true}
                            error={binding.isDuplicate}
                            selection={true}
                            value={binding.kceVersion.id}
                            options={versionOptions}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={(e: any, data: any) =>
                              this.handleKceVersionChange(data.value, index)
                            }
                            disabled={permissions}
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          id={`product-${index}`}
                          compact={true}
                          fluid={true}
                          error={binding.isDuplicate}
                          selection={true}
                          value={binding.kceProduct.id}
                          options={productOptions}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onChange={(e: any, data: any) =>
                            this.handleKceProductChange(data.value, index)
                          }
                          disabled={permissions}
                        />
                      </Table.Cell>
                      {!permissions && (
                        <Table.Cell>
                          <Popup
                            trigger={
                              <Icon
                                id={`remove-${index}`}
                                className="color-red"
                                link={true}
                                name="remove"
                                onClick={() => this.handleRemoveBinding(index)}
                              />
                            }
                            content={"Remove binding"}
                          />
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Add new binding handler
  private handleAddBinding = (): void => {
    const { code, bindings, handleBindingsChanged } = this.props;
    const newBinding: Api.LiftParameterBinding = {
      liftParameterCode: code,
      kceProduct: {
        id: -1,
        url: ""
      },
      kceVersion: {
        id: -1,
        url: ""
      },
      isDirty: true
    };
    handleBindingsChanged([newBinding, ...bindings]);
  };

  // Remove binding handler
  private handleRemoveBinding = (removedIndex: number): void => {
    const { bindings, handleBindingsChanged } = this.props;
    const modifiedBindings = bindings.filter((value, index) => index !== removedIndex);
    handleBindingsChanged(modifiedBindings);
  };

  // Change version handler
  private handleKceVersionChange = (kceVersionId: number, changedIndex: number): void => {
    const { bindings, kceVersions, handleBindingsChanged } = this.props;
    const kceVersion = kceVersions[kceVersionId];
    if (kceVersion) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            kceVersion: { id: kceVersion.id, url: kceVersion.url },
            isDirty: true
          };
        } else {
          return binding;
        }
      });
      handleBindingsChanged(editedBindings);
    }
  };

  // Change product handler
  private handleKceProductChange = (kceProductId: number, changedIndex: number): void => {
    const { bindings, kceProducts, handleBindingsChanged } = this.props;
    const kceProduct = kceProducts[kceProductId];
    if (kceProduct) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            kceProduct: { id: kceProduct.id, url: kceProduct.url },
            isDirty: true
          };
        } else {
          return binding;
        }
      });
      handleBindingsChanged(editedBindings);
    }
  };
}

export default LiftParameterBindings;
